import * as Dialogo from "@radix-ui/react-dialog";
import { AlertCircle, X } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { Botao } from "./Botao";
import { useDevolverFoco } from "./useDevolverFoco";

/*
 * O modal de cadastro — change 20d.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ CENTRADO, E NÃO LATERAL COMO O `PainelLateral`.                          │
 * │                                                                          │
 * │ Não é preferência estética: os dois diálogos têm relações opostas com a  │
 * │ tela de trás.                                                            │
 * │                                                                          │
 * │ O painel lateral existe para o operador COMPARAR — ele abre um boleto,   │
 * │ lê, fecha, abre o de baixo, e precisa da lista no mesmo lugar. O modal   │
 * │ de cadastro é o contrário: enquanto ele está aberto, a lista atrás não   │
 * │ tem nada a dizer. Preservá-la à vista sugeriria uma relação entre o que  │
 * │ se digita e a linha que ficou embaixo, e não há nenhuma.                 │
 * │                                                                          │
 * │ Some daí a diferença de largura: 420px, e não 520px — um formulário de   │
 * │ quatro campos numa caixa larga vira campo largo, e campo largo mente     │
 * │ sobre o tamanho do que se espera dentro dele. Uma agência tem quatro     │
 * │ dígitos.                                                                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * O Radix faz o trabalho invisível: prende o foco, fecha com Esc, e marca o resto
 * da página como inerte para leitor de tela. Devolver o foco a quem abriu é com
 * `useDevolverFoco`.
 */
export function ModalDeCadastro({
  aberto,
  aoFechar,
  titulo,
  descricao,
  children,
  aoEnviar,
  enviando,
  erro,
  rotuloDeEnvio = "Cadastrar",
  somenteLeitura = false,
}: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  /**
   * Linha de DADO sob o título — de quem é a conta, qual a aplicação —, e
   * nunca frase explicando o formulário. Sem ela, o diálogo é anunciado só
   * pelo título.
   */
  descricao?: string;
  children: ReactNode;
  aoEnviar: () => void;
  enviando: boolean;
  /** A recusa da API, já traduzida por `textoDoErro`. */
  erro?: string | undefined;
  rotuloDeEnvio?: string;
  /** Tudo travado, ou nada a mostrar: sem envio, cancelar e fechar seriam o mesmo botão. */
  somenteLeitura?: boolean;
}) {
  const devolverFoco = useDevolverFoco(aberto);

  function enviar(evento: FormEvent) {
    // Sem isto o navegador navega, a página recarrega e a sessão — que vive em
    // memória — morre. Num painel cujo access token nunca toca o disco, um
    // submit não interceptado desloga o operador.
    evento.preventDefault();
    aoEnviar();
  }

  return (
    <Dialogo.Root open={aberto} onOpenChange={(estado) => !estado && aoFechar()}>
      <Dialogo.Portal>
        {/*
          Acima do painel lateral (z-50), de onde a edição do convênio abre: abaixo
          dele, a sobreposição deixaria o painel aceso, parecendo clicável, e o clique
          nele fecharia o modal. Abaixo da confirmação (z-60), que este modal abre.
        */}
        <Dialogo.Overlay className="sobreposicao fixed inset-0 z-[56] bg-black/50" />

        <Dialogo.Content
          // Sem descrição, o Radix pede `undefined` explícito para não avisar
          // no console de uma descrição que falta de propósito.
          {...(descricao === undefined ? { "aria-describedby": undefined } : {})}
          onCloseAutoFocus={devolverFoco}
          className={[
            "fixed left-1/2 top-1/2 z-[57] w-[420px] max-w-[calc(100vw-2rem)]",
            "-translate-x-1/2 -translate-y-1/2",
            "max-h-[calc(100vh-4rem)] overflow-y-auto",
            "rounded-controle border border-fio bg-superficie",
          ].join(" ")}
        >
          <form onSubmit={enviar} className="flex flex-col">
            <header className="flex items-start justify-between gap-3 border-b border-fio px-4 py-3">
              <div className="flex flex-col gap-0.5">
                <Dialogo.Title className="text-corpo font-semibold text-texto">
                  {titulo}
                </Dialogo.Title>
                {descricao !== undefined && (
                  <Dialogo.Description className="rotulo">{descricao}</Dialogo.Description>
                )}
              </div>

              <Dialogo.Close
                type="button"
                aria-label="Fechar"
                className="rounded-controle p-1 text-texto-3 hover:bg-superficie-2 hover:text-texto"
              >
                <X aria-hidden="true" className="size-4" />
              </Dialogo.Close>
            </header>

            <div className="flex flex-col gap-3 px-4 py-4">{children}</div>

            {/*
              A recusa da API fica ACIMA dos botões, e não num aviso que some.
              Um `422` de "documento inválido" é para ler e corrigir ali mesmo;
              num toast, ele desapareceria antes de o operador achar o campo.
            */}
            {erro !== undefined && (
              <p
                role="alert"
                className="mx-4 mb-3 flex items-start gap-2 rounded-controle border border-falha bg-falha/10 px-3 py-2 text-corpo text-falha"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                {erro}
              </p>
            )}

            <footer className="flex justify-end gap-2 border-t border-fio px-4 py-3">
              {somenteLeitura ? (
                <Botao onClick={aoFechar}>Fechar</Botao>
              ) : (
                <>
                  <Botao tom="secundario" onClick={aoFechar}>
                    Cancelar
                  </Botao>
                  {/*
                    `type="submit"` chega por `...resto` e sobrescreve o `type="button"`
                    que o `Botao` põe por padrão — o padrão existe para que um botão de
                    ação dentro de formulário não o envie sem ninguém pedir, e aqui
                    enviar é exatamente o que se pede.
                  */}
                  <Botao type="submit" carregando={enviando}>
                    {rotuloDeEnvio}
                  </Botao>
                </>
              )}
            </footer>
          </form>
        </Dialogo.Content>
      </Dialogo.Portal>
    </Dialogo.Root>
  );
}
